import React, { useState, useEffect, useRef } from 'react';
import { activitiesAPI } from '../../services/api';
import { parseCoordinates } from '../../utils/coordinateParser';

import AgeRange from '../Common/AgeRange';
import CategorySelect from '../Common/CategorySelect';
import BasicInfoForm from './BasicInfoForm';
import LocationDefForm from './LocationDefForm';
import PropertiesForm from './PropertiesForm';

import FieldInfo from '../Common/FieldInfo';
import EventScheduleManager from './EventScheduleManager';

import { GoToDialog, ValidationError } from '../Common/Dialog';

import info from './activityFormInfoTexts';

import { Manual, Check, Reccuring, ArrowR, ArrowL } from '../Icons/CustomIcons';

// Kategorienliste - könnte später auch von Backend kommen
const categories = [
  'Sport & Bewegung',
  'Kultur & Kunst',
  'Natur & Tiere',
  'Museen & Ausstellungen',
  'Spielplätze',
  'Schwimmbäder',
  'Forschung & Experiment',
  'Workshops'
];

// Hilfsfunktion, um die Adresse in ihre Bestandteile zu zerlegen
const parseAddress = (address) => {
  const addr = (address ?? '').trim();
  const commaIdx = addr.indexOf(', ');
  if (commaIdx === -1) return { locationStreet: addr, locationPlz: '', locationCity: '' };
  const street = addr.substring(0, commaIdx);
  const rest = addr.substring(commaIdx + 2);
  const spaceIdx = rest.indexOf(' ');
  if (spaceIdx === -1) return { locationStreet: street, locationPlz: rest, locationCity: '' };
  return { locationStreet: street, locationPlz: rest.substring(0, spaceIdx), locationCity: rest.substring(spaceIdx + 1) };
};

//
const mapActivityToFormData = (activity) => ({
  name: activity?.name ?? '',
  description: activity?.description ?? '',
  locationName: activity?.location_name ?? '',
  ...parseAddress(activity?.address),
  latitude: activity?.latitude != null ? String(activity.latitude) : '',
  longitude: activity?.longitude != null ? String(activity.longitude) : '',
  coordinateString: (activity?.latitude != null && activity?.longitude != null)
    ? `${activity.latitude}, ${activity.longitude}`
    : '',
  website: activity?.website ?? '',
  locationWebsite: activity?.location_website ?? '',
  categories: activity?.categories ?? [],
  ageMin: activity?.age_min ?? 0,
  ageMax: activity?.age_max ?? 12,
  weatherType: activity?.indoor_outdoor ?? 'indoor',
  cost: activity?.cost_range ?? 'free',
  duration: activity?.duration ?? 'short',
  wheelchairAccessible: !!activity?.wheelchair_accessible,
  eventType: activity?.event_type ?? 'regular_hours',
  openingHours: activity?.opening_hours ?? [],
  eventSchedule: activity?.event_schedule ?? {},
  googlePlaceId: activity?.google_place_id ?? '',
  isSeasonal: !!activity?.is_seasonal,
  openingHoursMode: activity?.opening_hours_mode ?? '',
});

const ActivityHeaderCreate = () => (
  <div className="modal-header">
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <h2 className="drawer-title">Aktivität erstellen</h2>
      <FieldInfo>{info.pageTitle}</FieldInfo>
    </div>
    <p className="drawer-subtitle">Teilen Sie Ihre Aktivität mit anderen Grosseltern und Familien</p>
  </div>
);

const ActivityHeaderEdit = () => (
  <div className="modal-header">
    <h2 className="drawer-title">Aktivität bearbeiten</h2>
    <p className="drawer-subtitle">Ändern Sie die Angaben nach Bedarf. Nach erfolgreicher Prüfung werden Ihre Änderungen veröffentlicht.</p>
  </div>
);

// Hauptkomponente für das Erstellen/Bearbeiten einer Aktivität
const ActivityForm = ({ onSuccess, mode = 'create', initialData, activityId, onCancel }) => {
  const isEditMode = mode === 'edit';
  const [formData, setFormData] = useState(() => mapActivityToFormData(initialData));
  // Ref to always hold the latest eventSchedule — useState is async and would be stale
  // inside handleSubmit after runValidation() calls onEventScheduleChange.
  const eventScheduleRef = useRef(mapActivityToFormData(initialData).eventSchedule);
  // Same pattern for openingHours — setFormData is async and would be stale in handleSubmit.
  const openingHoursRef = useRef(mapActivityToFormData(initialData).openingHours);
  // Kombinierte Adresse für Validierung und Anzeige
  const combinedAddress = [
    formData?.locationStreet?.trim(),
    [formData?.locationPlz?.trim(), formData?.locationCity?.trim()].filter(Boolean).join(' ')
  ].filter(Boolean).join(', ');
  //
  const [locationInputMode, setLocationInputMode] = useState(() => {
    const initial = mapActivityToFormData(initialData);
    const hasCoords = initial.latitude !== '' && initial.longitude !== '';
    const hasRealAddress = (initial.locationStreet || '').trim() !== '' || (initial.locationCity || '').trim() !== '';
    return hasCoords && !hasRealAddress ? 'coordinates' : 'address';
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isResettingFormRef = useRef(false);
  const formRef = useRef(null);
  const eventScheduleSectionRef = useRef(null);
  const categoriesSectionRef = useRef(null);
  const chipScrollRef = useRef(null);
  const chipDragRef = useRef(false);

  const scrollToRef = (ref) => {
    requestAnimationFrame(() => {
      if (ref?.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  // Setze benutzerdefinierte Validierungsnachrichten auf Deutsch
  const handleGermanInvalidMessage = (e) => {
    const { validity, type } = e.target;

    if (validity.valueMissing) {
      e.preventDefault();
      e.target.setCustomValidity(' ');
      if (step === 1) setStep1Error('Pflichtfeld muss ausgefüllt werden.');
      if (step === 2) setStep2Error('Pflichtfeld muss ausgefüllt werden.');
      return;
    }

    if (type === 'url' && validity.typeMismatch) {
      e.target.setCustomValidity('Bitte geben Sie eine gültige URL ein (z.B. https://beispiel.ch).');
      return;
    }

    e.target.setCustomValidity('');
  };

  // Validierung der Adresse über Geocoding-API
  const clearCustomValidityMessage = (e) => {
    e.target.setCustomValidity('');
    if (step === 1) setStep1Error(null);
    if (step === 2) setStep2Error(null);
  };
  
  // Setze timeDataSaved initial auf true wenn Aktivität geladen wird und Zeitdaten vorhanden sind
  const [, setTimeDataSaved] = useState(() => {
    if (isEditMode && initialData) {
      const eventType = initialData.event_type;
      if (eventType === 'regular_hours') {
        return initialData.opening_hours && initialData.opening_hours.length > 0;
      } else {
        return initialData.event_schedule && Object.keys(initialData.event_schedule).length > 0;
      }
    }
    return false;
  });
  const [step, setStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showEditSuccess, setShowEditSuccess] = useState(false);
  const [editSuccessData, setEditSuccessData] = useState(null);
  const [createdActivityId, setCreatedActivityId] = useState(null);
  const scheduleValidateRef = useRef(null);
  const addressValidateRef = useRef(null);
  const [step3Error, setStep3Error] = useState(null);
  const [step1Error, setStep1Error] = useState(null);
  const [step2Error, setStep2Error] = useState(null);
  const [step4Error, setStep4Error] = useState(null);

  // Track form changes to warn before navigation
  useEffect(() => {
    if (isResettingFormRef.current) {
      isResettingFormRef.current = false;
      return;
    }
    setIsDirty(true);
  }, [formData]);

  // Block browser back button when form has unsaved changes
  useEffect(() => {
    if (!isDirty) return;
    // Push a sentinel state (same URL) so the first back press stays on this page
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      if (window.confirm('Sie haben ungespeicherte Änderungen. Möchten Sie die Seite wirklich verlassen?')) {
        window.removeEventListener('popstate', handlePopState);
        setIsDirty(false);
        window.history.back();
      } else {
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty]);

  // Warn on page refresh / tab close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    isResettingFormRef.current = true;
    const initial = mapActivityToFormData(initialData);
    eventScheduleRef.current = initial.eventSchedule;
    openingHoursRef.current = initial.openingHours;
    setFormData(initial);
    const hasCoords = initial.latitude !== '' && initial.longitude !== '';
    const hasRealAddress = (initial.locationStreet || '').trim() !== '' || (initial.locationCity || '').trim() !== '';
    setLocationInputMode(hasCoords && !hasRealAddress ? 'coordinates' : 'address');
    setIsDirty(false);
    
    // Aktualisiere timeDataSaved wenn initialData sich ändert
    if (isEditMode && initialData) {
      const eventType = initialData.event_type;
      if (eventType === 'regular_hours') {
        setTimeDataSaved(initialData.opening_hours && initialData.opening_hours.length > 0);
      } else {
        setTimeDataSaved(initialData.event_schedule && Object.keys(initialData.event_schedule).length > 0);
      }
    }
  }, [initialData, isEditMode]);

  // Drag-to-scroll for chip navigation bar (desktop)
  useEffect(() => {
    if (!isEditMode) return;
    const el = chipScrollRef.current;
    if (!el) return;
    let isDown = false, startX = 0, scrollLeft = 0, moved = false;
    const onMouseDown = (e) => {
      isDown = true; moved = false;
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
    };
    const onMouseLeave = () => { isDown = false; el.style.cursor = 'grab'; el.style.userSelect = ''; };
    const onMouseUp = () => {
      chipDragRef.current = moved;
      isDown = false; el.style.cursor = 'grab'; el.style.userSelect = '';
    };
    const onMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const delta = x - startX;
      if (Math.abs(delta) > 4) moved = true;
      el.scrollLeft = scrollLeft - delta;
    };
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('mousemove', onMouseMove);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('mousemove', onMouseMove);
    };
  }, [isEditMode]);

  // Scroll the active wizard chip into view (centered) when step changes or on initial open.
  // Uses rAF so getBoundingClientRect works after the drawer has painted.
  useEffect(() => {
    if (!isEditMode) return;
    const raf = requestAnimationFrame(() => {
      const container = chipScrollRef.current;
      if (!container) return;
      const activeChip = container.querySelector('.wizard-chip.active');
      if (!activeChip) return;
      const containerRect = container.getBoundingClientRect();
      const chipRect = activeChip.getBoundingClientRect();
      const offset = (chipRect.left + chipRect.width / 2) - (containerRect.left + containerRect.width / 2);
      container.scrollTo({ left: container.scrollLeft + offset, behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [isEditMode, step]);

  const validateStep = async (n) => {
    if (n === 1) {
      if (!formData.name.trim()) return 'Bitte geben Sie einen Namen ein.';
      if (!formData.description.trim()) return 'Bitte geben Sie eine Beschreibung ein.';
    }
    if (n === 2) {
      if (!formData.locationName.trim()) return 'Bitte geben Sie eine Ortsbezeichnung ein.';
      if (locationInputMode === 'address') {
        if (!formData.locationStreet.trim()) return 'Bitte geben Sie eine Strasse ein.';
        const addrErr = await addressValidateRef.current?.();
        if (addrErr) return addrErr;
      }
      if (locationInputMode === 'coordinates' && (!formData.latitude || !formData.longitude))
        return 'Koordinaten konnten nicht erkannt werden. Bitte im richtigen Format eingeben.';
    }
    if (n === 3) {
      const err = await scheduleValidateRef.current?.();
      if (err) return err;
    }
    if (n === 4) {
      if (formData.categories.length === 0) return 'Bitte wählen Sie mindestens eine Kategorie aus.';
    }
    return null;
  };

  const handleChipClick = async (targetStep) => {
    if (chipDragRef.current) { chipDragRef.current = false; return; }
    if (targetStep === step) return;
    const err = await validateStep(step);
    if (err) {
      setError(err);
      if (step === 3) setStep3Error(err);
      if (step === 4) setStep4Error(err);
      return;
    }
    setError('');
    setStep3Error(null);
    setStep4Error(null);
    setStep(targetStep);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isEditMode) {
      for (const n of [1, 2, 4]) {
        const err = await validateStep(n);
        if (err) {
          setError(err);
          if (n === 4) setStep4Error(err);
          setStep(n);
          return;
        }
      }
      if (step === 3) {
        const err3 = await validateStep(3);
        if (err3) {
          setError(err3);
          setStep3Error(err3);
          return;
        }
      }
    }

    const hasAddress = combinedAddress !== '';
    const parsedCoords = locationInputMode === 'coordinates'
      ? parseCoordinates(formData.coordinateString)
      : null;
    const latitude = parsedCoords?.lat ?? parseFloat(formData.latitude);
    const longitude = parsedCoords?.lng ?? parseFloat(formData.longitude);
    const hasValidCoordinates = parsedCoords !== null;

    if (locationInputMode === 'address') {
      if (!hasAddress) {
        scrollToRef(formRef);
        return;
      }
    }

    if (locationInputMode === 'coordinates') {
      if (!hasValidCoordinates) {
        setError('Koordinaten konnten nicht erkannt werden. Bitte im Format "47.4979, 8.7241" (Dezimal), "47°29\'52"N 8°43\'24"E" (DMS) oder "2699163, 1261335" (LV95) eingeben.');
        scrollToRef(formRef);
        return;
      }
    }

    // Validierung
    if (!isEditMode) {
      if (formData.categories.length === 0) {
        setError('Bitte wählen Sie mindestens eine Kategorie aus.');
        setStep4Error('Bitte wählen Sie mindestens eine Kategorie aus.');
        setStep(4);
        return;
      }
      
      // Validiere Zeitangaben
      const err3 = await validateStep(3);
      if (err3) {
        setError(err3);
        setStep3Error(err3);
        scrollToRef(eventScheduleSectionRef);
        return;
      }
    }

    // Spezielle Validierung für single_event
    if (formData.eventType === 'single_event') {
      const currentSchedule = eventScheduleRef.current;
      if (currentSchedule && currentSchedule.dates) {
        const hasValidDate = currentSchedule.dates.some(d => d.date && d.date.trim() !== '');
        if (!hasValidDate) {
          setError('Bitte geben Sie mindestens ein Datum für den Einzeltermin ein');
          scrollToRef(eventScheduleSectionRef);
          return;
        }
      } else {
        setError('Bitte geben Sie mindestens ein Datum für den Einzeltermin ein');
        scrollToRef(eventScheduleSectionRef);
        return;
      }
    }

    setLoading(true);
    
    try {
      // Verwende FormData für File-Upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);

      if (locationInputMode === 'address') {
        formDataToSend.append('address', combinedAddress);
        formDataToSend.append('location_street', formData.locationStreet.trim());
        formDataToSend.append('location_plz', formData.locationPlz.trim());
        formDataToSend.append('location_city', formData.locationCity.trim());
      }
      if (locationInputMode === 'coordinates' && hasValidCoordinates) {
        formDataToSend.append('latitude', String(latitude));
        formDataToSend.append('longitude', String(longitude));
      }
      formDataToSend.append('location_name', formData.locationName);
      formDataToSend.append('age_min', formData.ageMin);
      formDataToSend.append('age_max', formData.ageMax);
      formDataToSend.append('indoor_outdoor', formData.weatherType);
      formDataToSend.append('cost_range', formData.cost);
      formDataToSend.append('duration', formData.duration);
      formDataToSend.append('website', formData.website);
      formDataToSend.append('location_website', formData.locationWebsite);
      formDataToSend.append('categories', JSON.stringify(formData.categories));
      formDataToSend.append('wheelchair_accessible', formData.wheelchairAccessible);
      formDataToSend.append('event_type', formData.eventType);
      formDataToSend.append('is_seasonal', formData.isSeasonal);
      if (formData.googlePlaceId) {
        formDataToSend.append('google_place_id', formData.googlePlaceId);
      }
      
      // Sende nur die relevanten Zeitdaten basierend auf event_type
      if (formData.eventType === 'regular_hours') {
        // Nur Öffnungszeiten relevant
        formDataToSend.append('opening_hours', JSON.stringify(openingHoursRef.current));
        formDataToSend.append('event_schedule', JSON.stringify({}));
      } else if (formData.eventType === 'recurring_event') {
        // Nur event_schedule für wiederkehrende Events
        formDataToSend.append('opening_hours', JSON.stringify([]));
        formDataToSend.append('event_schedule', JSON.stringify(eventScheduleRef.current));
      } else if (formData.eventType === 'single_event') {
        // Nur event_schedule für Einzeltermine
        formDataToSend.append('opening_hours', JSON.stringify([]));
        formDataToSend.append('event_schedule', JSON.stringify(eventScheduleRef.current));
      } else if (formData.eventType === 'multi_day') {
        // Nur event_schedule für mehrtägige Events
        formDataToSend.append('opening_hours', JSON.stringify([]));
        formDataToSend.append('event_schedule', JSON.stringify(eventScheduleRef.current));
      } else if (formData.eventType === 'freely_accessible') {
        // Nur event_schedule für frei zugängliche Events
        formDataToSend.append('opening_hours', JSON.stringify([]));
        formDataToSend.append('event_schedule', JSON.stringify(eventScheduleRef.current));
        
        // Füge Zugänglichkeitsdaten hinzu (wenn vorhanden)
        const currentSchedule = eventScheduleRef.current;
        if (currentSchedule) {
          if (currentSchedule.accessible_from) {
            formDataToSend.append('accessible_from', currentSchedule.accessible_from);
          }
          if (currentSchedule.accessible_until) {
            formDataToSend.append('accessible_until', currentSchedule.accessible_until);
          }
        }
      }
      
      if (isEditMode) {
        const response = await activitiesAPI.update(activityId, formDataToSend);
        setIsDirty(false);
        setEditSuccessData(response?.data);
        setShowEditSuccess(true);
      } else {
        const response = await activitiesAPI.create(formDataToSend);
        setIsDirty(false);
        setCreatedActivityId(response?.data?.id ?? null);
        setShowSuccess(true);
      }
    } catch (error) {
      console.error('Error saving activity:', error);
      const errorMsg = error.response?.data?.error || (isEditMode
        ? 'Fehler beim Aktualisieren der Aktivität'
        : 'Fehler beim Erstellen der Aktivität');
      setError(errorMsg);
      scrollToRef(formRef);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
    setStep4Error(null);
  };

  const STEPS = [
    { n: 1, title: 'Aktivität', prev: null,                next: 'Weiter: Standort' },
    { n: 2, title: 'Standort',           prev: 'Aktivität', next: 'Datum & Uhrzeit' },
    { n: 3, title: 'Datum & Uhrzeit',        prev: 'Standort',           next: 'Kategorisierung' },
    { n: 4, title: 'Kategorisierung',    prev: 'Datum & Uhrzeit',        next: 'Eigenschaften' },
    { n: 5, title: 'Eigenschaften',      prev: 'Kategorisierung',    next: null },
  ];

  const canProceed =
    step === 1 ? (formData.name.trim().length > 0 && formData.description.trim().length > 0) :
    step === 2 ? (
      formData.locationName.trim().length > 0 &&
      (locationInputMode === 'address'
        ? formData.locationStreet.trim().length > 0
        : (formData.latitude !== '' && formData.longitude !== ''))
    ) :
    step === 3 ? true :
    step === 4 ? formData.categories.length > 0 :
    true;

  // Success screen after creation
  if (showSuccess) {
    return (
      <GoToDialog
        icon={Check}
        title="Aktivität erstellt"
        text="Aktivität ansehen"
        message="Sie können die Aktivität ansehen und bearbeiten. Sie wird vor der Veröffentlichung überprüft."
        onConfirm={() => onSuccess?.(createdActivityId)}
      />
    );
  }

  return (
    <>
    {showEditSuccess && (   
      <GoToDialog
        icon={Manual}
        title="Änderungen gespeichert"
        text="Zur Aktivität"
        message="Nach der Prüfung werden Ihre Änderungen für Nutzer sichtbar."
        onConfirm={() => { setShowEditSuccess(false); onSuccess?.(editSuccessData); }}
      />
    )}    

    {isEditMode ? <ActivityHeaderEdit /> : <ActivityHeaderCreate />}

    {/* Wizard header */}
    <div className="wizard-header">
      {isEditMode ? (
        <div
          ref={chipScrollRef}
          className="wizard-chip-scroll"
        >
          {STEPS.map(s => (
            <button
              key={s.n}
              type="button"
              onClick={() => handleChipClick(s.n)}
              className={`wizard-chip ${step === s.n ? 'active' : ''}`}              >
              {s.title}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="wizard-step-info">
            <p className="wizard-step-info__text">Schritt {step} von 5: {STEPS[step - 1].title}</p>
            {step === 3 && (
              <FieldInfo>
                <>
                  Events erfordern je nach Typ unterschiedliche Formate für Zeitangaben.
                  Details und Beispiele finden Sie auf der Seite{' '}
                  <a href="/zeitangaben-formate" style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
                    Formate für Zeitangaben
                  </a>
                  .
                </>
              </FieldInfo>
            )}
          </div>
        </>
      )}
    </div>
    <form ref={formRef} onSubmit={handleSubmit} className="modal-content-scrollable">
      {error && <div className="error-message">{error}</div>}


      {/* STEP 1: Grundinformationen */}
      {step === 1 && (
        <BasicInfoForm
          formData={formData}
          setFormData={setFormData}
          info={info}
          onInvalid={handleGermanInvalidMessage}
          onInput={clearCustomValidityMessage}
        />
      )}

      {/* STEP 2: Standort */}
      {step === 2 && (
        <LocationDefForm
          formData={formData}
          setFormData={setFormData}
          locationInputMode={locationInputMode}
          setLocationInputMode={setLocationInputMode}
          info={info}
          onInvalid={handleGermanInvalidMessage}
          onInput={clearCustomValidityMessage}
          validateRef={addressValidateRef}
        />
      )}

      {/* STEP 3: Zeitangaben */}
      {step === 3 && (
        <div ref={eventScheduleSectionRef} className="form-section--scrollable">
          <EventScheduleManager
            eventType={formData.eventType}
            openingHours={formData.openingHours}
            eventSchedule={formData.eventSchedule}
            isSeasonal={formData.isSeasonal}
            website={formData.locationWebsite}
            isEditMode={isEditMode}
            onWebsiteChange={(locationWebsite) => setFormData(prev => ({ ...prev, locationWebsite }))}
            onEventTypeChange={(type) => {
              setFormData(prev => ({ ...prev, eventType: type }));
              setTimeDataSaved(false);
              setStep3Error(null);
            }}
            onOpeningHoursChange={(hours) => {
              openingHoursRef.current = hours;
              setFormData(prev => ({ ...prev, openingHours: hours }));
              setStep3Error(null);
            }}
            onEventScheduleChange={(schedule) => {
              eventScheduleRef.current = schedule;
              setFormData(prev => ({ ...prev, eventSchedule: schedule }));
              setStep3Error(null);
            }}
            onSeasonalChange={(isSeasonal) => setFormData(prev => ({ ...prev, isSeasonal }))}
            initialOpeningHoursMode={formData.openingHoursMode}
            onOpeningHoursModeChange={(mode) => setFormData(prev => ({ ...prev, openingHoursMode: mode }))}
            onSaveStatusChange={(saved) => setTimeDataSaved(saved)}
            validateRef={scheduleValidateRef}
            onImportFromWebsite={async () => {
              if (!formData.locationWebsite) {
                throw new Error('Bitte geben Sie zuerst die Webseite der Location ein');
              }
              const result = await activitiesAPI.importOpeningHoursFromWebsite(
                formData.locationName,
                combinedAddress,
                formData.locationWebsite
              );
              const importedHours = result.data.opening_hours || [];
              openingHoursRef.current = importedHours;
              setFormData(prev => ({
                ...prev,
                openingHours: importedHours,
                googlePlaceId: result.data.google_place_id
              }));
              return result.data;
            }}
            onImportOpeningHours={async () => {
              if (!activityId) {
                throw new Error('Aktivität muss zuerst gespeichert werden');
              }
              const result = await activitiesAPI.importOpeningHours(activityId);
              if (result.data.opening_hours) {
                openingHoursRef.current = result.data.opening_hours;
                setFormData(prev => ({
                  ...prev,
                  openingHours: result.data.opening_hours,
                  googlePlaceId: result.data.google_place_id || prev.googlePlaceId
                }));
              }
              return result.data;
            }}
            address={combinedAddress}
            canImportFromGoogle={!!activityId}
          />
        </div>
      )}

      {/* STEP 4: Kategorisierung */}
      {step === 4 && (
        <div ref={categoriesSectionRef} className="form-section--scrollable">
          <div className="modal-content">
            <AgeRange
              minValue={formData.ageMin}
              maxValue={formData.ageMax}
              onChange={(min, max) => setFormData(prev => ({ ...prev, ageMin: min, ageMax: max }))}
              fieldInfo={info.ageRange}
            />
            <CategorySelect
              options={categories.map(c => ({ value: c, label: c }))}
              selected={formData.categories}
              onToggle={toggleCategory}
              fieldInfo={info.categories}
            />
          </div>
        </div>
      )}

      {/* STEP 5: Eigenschaften */}
      {step === 5 && (
        <div className="form-section--scrollable">
          <PropertiesForm
            formData={formData}
            setFormData={setFormData}
            info={info}
          />
        </div>
      )}

      {/* Navigation (wizard) */}
      <div className="wizard-nav">
      {isEditMode ? (
        <>
          {step === 1 && step1Error && (
            <ValidationError className="wizard-error">{step1Error}</ValidationError>
          )}
          {step === 2 && step2Error && (
            <ValidationError className="wizard-error">{step2Error}</ValidationError>
          )}
          {step === 3 && step3Error && (
            <ValidationError className="wizard-error">{step3Error}</ValidationError>
          )}
          {step === 4 && step4Error && (
            <ValidationError className="wizard-error">{step4Error}</ValidationError>
          )}
          <button key="wizard-submit" type="submit" disabled={loading} className="btn btn-primary btn-m submit-btn" style={{ marginLeft: 'auto' }}>
            {loading
              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><Reccuring size={24} /><span>Wird gespeichert...</span></span>
              : <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><Check size={24} /><span>Änderungen speichern</span></span>}
          </button>
        </>
      ) : (
        <>
          {step === 1 && step1Error && (
            <ValidationError className="wizard-error">{step1Error}</ValidationError>
          )}
          {step === 2 && step2Error && (
            <ValidationError className="wizard-error">{step2Error}</ValidationError>
          )}
          {step === 3 && step3Error && (
            <ValidationError className="wizard-error">{step3Error}</ValidationError>
          )}
          {step === 4 && step4Error && (
            <ValidationError className="wizard-error">{step4Error}</ValidationError>
          )}
          {STEPS[step - 1].prev ? (
            <button type="button" className="btn btn-secondary btn-m wizard-back-btn" onClick={() => setStep(s => s - 1)}>
              <ArrowL size={24} />
              <span className="wizard-back-label">{STEPS[step - 1].prev}</span>
            </button>
          ) : (
            <span />
          )}
          {STEPS[step - 1].next ? (
            <button
              key="wizard-next"
              type="button"
              className="btn btn-primary btn-m wizard-next-btn"
              disabled={!canProceed}
              onClick={async () => {
                if (step === 2) {
                  const err = await validateStep(2);
                  if (err) { setError(err); return; }
                  setError('');
                }
                if (step === 3) {
                  const err = await scheduleValidateRef.current?.();
                  if (err) { setStep3Error(err); return; }
                  setStep3Error(null);
                }
                setStep(s => s + 1);
              }}
            >
              {STEPS[step - 1].next}
              <ArrowR size={24} style={{ marginLeft: '0.3rem' }} />
            </button>
          ) : (
            <button key="wizard-submit" type="submit" disabled={loading} className="btn btn-primary btn-m wizard-next-btn submit-btn">
              {loading ? 'Wird erstellt...' : <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><Check size={24} /><span>Aktivität erstellen</span></span>}
            </button>
          )}
        </>
      )}
      </div>
    </form>
    </>
  );
};

export default ActivityForm;
